import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import DocumentService, {
  PatientDocument,
} from "../../../../services/documentService";
import DocumentViewer from "./DocumentViewer";

interface DocumentManagerProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  refreshTrigger?: number;
}

const DocumentManager: React.FC<DocumentManagerProps> = ({
  patient,
  isOpen,
  onClose,
  refreshTrigger,
}) => {
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<PatientDocument[]>(
    []
  );
  const [selectedDocument, setSelectedDocument] =
    useState<PatientDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const categories = [
    { value: "", label: "All Categories" },
    { value: "lab_result", label: "Lab Results" },
    { value: "imaging", label: "Imaging" },
    { value: "dicom", label: "DICOM/Medical Images" },
    { value: "prescription", label: "Prescriptions" },
    { value: "report", label: "Reports" },
    { value: "consent", label: "Consent Forms" },
    { value: "insurance", label: "Insurance" },
    { value: "referral", label: "Referrals" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && patient.id) {
      loadDocuments();
    }
  }, [isOpen, patient.id, refreshTrigger]);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm, selectedCategory]);

  const loadDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await DocumentService.getPatientDocuments(patient.id);
      setDocuments(result);
    } catch (err: any) {
      setError(err.message || "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents;

    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.tags?.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (doc) => doc.document_type === selectedCategory
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleViewDocument = (document: PatientDocument) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  };

  const handleDownloadDocument = async (patientDoc: PatientDocument) => {
    try {
      const { blob, filename } = await DocumentService.downloadDocument(
        patientDoc.id!
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let downloadFilename = filename;
      if (!downloadFilename.includes(".")) {
        const extension = patientDoc.file_name?.split(".").pop();
        if (extension) {
          downloadFilename += "." + extension;
        }
      }

      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Failed to download document: ${err.message}`);
    }
  };

  const handleDeleteDocument = async (document: PatientDocument) => {
    if (
      !window.confirm(`Are you sure you want to delete "${document.title}"?`)
    ) {
      return;
    }

    try {
      await DocumentService.deleteDocument(document.id!);
      setDocuments((prev) => prev.filter((doc) => doc.id !== document.id));
      // alert("Document deleted successfully");
    } catch (err: any) {
      alert(`Failed to delete document: ${err.message}`);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "8px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "1400px",
    height: isMobile ? "100vh" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : "24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    flexShrink: 0,
  };

  const filtersStyle: React.CSSProperties = {
    padding: isMobile ? "12px 16px" : "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    display: "flex",
    gap: isMobile ? "8px" : "16px",
    alignItems: "center",
    flexWrap: "wrap",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#f59e0b", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: isMobile ? "6px 12px" : "8px 16px",
      borderRadius: "6px",
      border: "none",
      fontSize: isMobile ? "11px" : "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
    };
  };

  const inputStyle: React.CSSProperties = {
    padding: isMobile ? "6px 10px" : "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: isMobile ? "13px" : "14px",
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  return (
    <>
      <div
        style={overlayStyle}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div style={modalStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: isMobile ? "8px" : "0",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h2
                  style={{
                    fontSize: isMobile ? "16px" : "20px",
                    fontWeight: "600",
                    margin: 0,
                    marginBottom: "4px",
                  }}
                >
                  Document Manager
                </h2>
                <p
                  style={{
                    fontSize: isMobile ? "12px" : "14px",
                    color: "#64748b",
                    margin: 0,
                    wordBreak: "break-word",
                  }}
                >
                  Manage documents for{" "}
                  {patient.full_name ||
                    `${patient.first_name} ${patient.last_name}`}
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  padding: "8px 12px",
                  flexShrink: 0,
                }}
              >
                Close
              </button>
            </div>
          </div>

          {/* Filters */}
          <div style={filtersStyle}>
            <div
              style={{
                display: "flex",
                gap: isMobile ? "8px" : "12px",
                alignItems: "center",
                flex: 1,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <input
                type="text"
                id="search_documents"
                name="search_documents"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...inputStyle,
                  minWidth: isMobile ? "150px" : "200px",
                  flex: isMobile ? "1 1 150px" : "0 0 200px",
                }}
              />

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  ...inputStyle,
                  minWidth: isMobile ? "120px" : "150px",
                  flex: isMobile ? "1 1 120px" : "0 0 150px",
                }}
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: isMobile ? "12px" : "14px",
                  color: "#6b7280",
                  display: isMobile ? "none" : "inline",
                }}
              >
                View:
              </span>
              <button
                onClick={() => setViewMode("grid")}
                style={{
                  ...buttonStyle(viewMode === "grid" ? "primary" : "secondary"),
                  padding: isMobile ? "4px 8px" : "6px 12px",
                }}
              >
                Grid {!isMobile && "View"}
              </button>
              <button
                onClick={() => setViewMode("list")}
                style={{
                  ...buttonStyle(viewMode === "list" ? "primary" : "secondary"),
                  padding: isMobile ? "4px 8px" : "6px 12px",
                }}
              >
                List {!isMobile && "View"}
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid #f59e0b",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Loading documents...
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#ef4444",
                  padding: isMobile ? "20px" : "40px",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                Error: {error}
                <br />
                <button
                  onClick={loadDocuments}
                  style={{ ...buttonStyle("primary"), marginTop: "16px" }}
                >
                  Retry
                </button>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  padding: isMobile ? "20px" : "40px",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                {documents.length === 0 ? (
                  <>
                    No documents found for this patient
                    <br />
                    <p
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        marginTop: "8px",
                      }}
                    >
                      Upload documents to get started
                    </p>
                  </>
                ) : (
                  <>
                    No documents match your search criteria
                    <br />
                    <p
                      style={{
                        fontSize: isMobile ? "12px" : "14px",
                        marginTop: "8px",
                      }}
                    >
                      Try adjusting your search or filter settings
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Summary */}
                <div
                  style={{
                    marginBottom: "20px",
                    padding: isMobile ? "8px 12px" : "12px 16px",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: isMobile ? "12px" : "14px",
                    color: "#6b7280",
                  }}
                >
                  Showing {filteredDocuments.length} of {documents.length}{" "}
                  documents
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory &&
                    ` in ${
                      categories.find((c) => c.value === selectedCategory)
                        ?.label
                    }`}
                </div>

                {/* Documents */}
                {viewMode === "grid" ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: isMobile ? "12px" : "20px",
                    }}
                  >
                    {filteredDocuments.map((document) => (
                      <div
                        key={document.id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "12px",
                          overflow: "hidden",
                          transition: "all 0.2s ease",
                          background: "#ffffff",
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(0, 0, 0, 0.1)";
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }
                        }}
                      >
                        {/* Document Preview */}
                        <div
                          style={{
                            height: isMobile ? "80px" : "120px",
                            background:
                              DocumentService.getCategoryColor(
                                document.document_type
                              ) + "10",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            cursor: "pointer",
                          }}
                          onClick={() => handleViewDocument(document)}
                        >
                          <div style={{ fontSize: isMobile ? "32px" : "48px" }}>
                            {DocumentService.getFileTypeIcon(
                              document.mime_type
                            )}
                          </div>
                          {document.is_confidential && (
                            <div
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                background: "#fee2e2",
                                color: "#dc2626",
                                padding: isMobile ? "2px 6px" : "4px 8px",
                                borderRadius: "12px",
                                fontSize: isMobile ? "9px" : "10px",
                                fontWeight: "500",
                              }}
                            >
                              Confidential
                            </div>
                          )}
                        </div>

                        {/* Document Info */}
                        <div style={{ padding: isMobile ? "12px" : "16px" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                            }}
                          >
                            <h4
                              style={{
                                fontSize: isMobile ? "13px" : "14px",
                                fontWeight: "600",
                                margin: 0,
                                color: "#1f2937",
                                lineHeight: "1.4",
                                flex: 1,
                                wordBreak: "break-word",
                              }}
                            >
                              {document.title}
                            </h4>
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "8px",
                                fontSize: isMobile ? "9px" : "10px",
                                fontWeight: "500",
                                background:
                                  DocumentService.getCategoryColor(
                                    document.document_type
                                  ) + "20",
                                color: DocumentService.getCategoryColor(
                                  document.document_type
                                ),
                                marginLeft: "8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {DocumentService.getCategoryIcon(
                                document.document_type
                              )}
                            </span>
                          </div>

                          {document.description && (
                            <p
                              style={{
                                fontSize: isMobile ? "11px" : "12px",
                                color: "#6b7280",
                                margin: "0 0 8px 0",
                                lineHeight: "1.4",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {document.description}
                            </p>
                          )}

                          <div
                            style={{
                              fontSize: isMobile ? "10px" : "11px",
                              color: "#9ca3af",
                              marginBottom: "12px",
                              display: "flex",
                              justifyContent: "space-between",
                              flexWrap: isMobile ? "wrap" : "nowrap",
                              gap: isMobile ? "4px" : "0",
                            }}
                          >
                            <span>
                              {DocumentService.formatFileSize(
                                document.file_size
                              )}
                            </span>
                            <span>
                              {DocumentService.formatDate(document.upload_date)}
                            </span>
                          </div>

                          {/* Tags */}
                          {document.tags && document.tags.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px",
                                marginBottom: "12px",
                              }}
                            >
                              {document.tags
                                .slice(0, isMobile ? 2 : 3)
                                .map((tag) => (
                                  <span
                                    key={tag}
                                    style={{
                                      padding: "2px 6px",
                                      background: "#f3f4f6",
                                      color: "#6b7280",
                                      borderRadius: "6px",
                                      fontSize: isMobile ? "9px" : "10px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              {document.tags.length > (isMobile ? 2 : 3) && (
                                <span
                                  style={{
                                    padding: "2px 6px",
                                    background: "#f3f4f6",
                                    color: "#6b7280",
                                    borderRadius: "6px",
                                    fontSize: isMobile ? "9px" : "10px",
                                    fontWeight: "500",
                                  }}
                                >
                                  +{document.tags.length - (isMobile ? 2 : 3)}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div
                            style={{
                              display: "flex",
                              gap: isMobile ? "4px" : "6px",
                              borderTop: "1px solid #f3f4f6",
                              paddingTop: "12px",
                              flexWrap: isMobile ? "wrap" : "nowrap",
                            }}
                          >
                            <button
                              onClick={() => handleViewDocument(document)}
                              style={{
                                ...buttonStyle("primary"),
                                flex: isMobile ? "1 1 auto" : 1,
                                fontSize: isMobile ? "10px" : "11px",
                              }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDownloadDocument(document)}
                              style={{
                                ...buttonStyle("secondary"),
                                flex: isMobile ? "1 1 auto" : 1,
                                fontSize: isMobile ? "10px" : "11px",
                              }}
                            >
                              {isMobile ? "Download" : "Download"}
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(document)}
                              style={{
                                ...buttonStyle("danger"),
                                fontSize: isMobile ? "10px" : "11px",
                                padding: isMobile ? "6px" : "8px",
                                flex: isMobile ? "0 0 auto" : "0 0 auto",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // List View - Mobile optimized
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    {!isMobile && (
                      // List Header - Hide on mobile
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 2fr 1fr 1fr 1fr auto",
                          gap: "16px",
                          padding: "12px 16px",
                          background: "#f8fafc",
                          borderBottom: "1px solid #e5e7eb",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                          alignItems: "center",
                        }}
                      >
                        <div>Type</div>
                        <div>Name</div>
                        <div>Category</div>
                        <div>Size</div>
                        <div>Upload Date</div>
                        <div>Actions</div>
                      </div>
                    )}

                    {/* List Items */}
                    {filteredDocuments.map((document) => (
                      <div
                        key={document.id}
                        style={{
                          display: isMobile ? "block" : "grid",
                          gridTemplateColumns: isMobile
                            ? "none"
                            : "auto 2fr 1fr 1fr 1fr auto",
                          gap: isMobile ? "8px" : "16px",
                          padding: isMobile ? "16px" : "12px 16px",
                          borderBottom: "1px solid #f3f4f6",
                          fontSize: isMobile ? "13px" : "14px",
                          alignItems: "center",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        {isMobile ? (
                          // Mobile layout - stacked
                          <>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                marginBottom: "8px",
                              }}
                            >
                              <div style={{ fontSize: "24px" }}>
                                {DocumentService.getFileTypeIcon(
                                  document.mime_type
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    color: "#1f2937",
                                    cursor: "pointer",
                                    wordBreak: "break-word",
                                  }}
                                  onClick={() => handleViewDocument(document)}
                                >
                                  {document.title}
                                  {document.is_confidential && (
                                    <span
                                      style={{
                                        marginLeft: "6px",
                                        padding: "1px 4px",
                                        background: "#fee2e2",
                                        color: "#dc2626",
                                        borderRadius: "4px",
                                        fontSize: "9px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Confidential
                                    </span>
                                  )}
                                </div>
                                {document.description && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      lineHeight: "1.3",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {document.description.length > 40
                                      ? `${document.description.substring(
                                          0,
                                          40
                                        )}...`
                                      : document.description}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: "11px",
                                color: "#6b7280",
                                marginBottom: "8px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  background:
                                    DocumentService.getCategoryColor(
                                      document.document_type
                                    ) + "20",
                                  color: DocumentService.getCategoryColor(
                                    document.document_type
                                  ),
                                }}
                              >
                                {DocumentService.getCategoryIcon(
                                  document.document_type
                                )}
                              </span>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <span>
                                  {DocumentService.formatFileSize(
                                    document.file_size
                                  )}
                                </span>
                                <span>
                                  {DocumentService.formatDate(
                                    document.upload_date
                                  )}
                                </span>
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: "6px" }}>
                              <button
                                onClick={() => handleViewDocument(document)}
                                style={{
                                  ...buttonStyle("primary"),
                                  flex: 1,
                                  fontSize: "10px",
                                }}
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(document)}
                                style={{
                                  ...buttonStyle("secondary"),
                                  flex: 1,
                                  fontSize: "10px",
                                }}
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(document)}
                                style={{
                                  ...buttonStyle("danger"),
                                  fontSize: "10px",
                                  padding: "6px",
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        ) : (
                          // Desktop layout - grid
                          <>
                            <div style={{ fontSize: "20px" }}>
                              {DocumentService.getFileTypeIcon(
                                document.mime_type
                              )}
                            </div>

                            <div>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1f2937",
                                  marginBottom: "2px",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleViewDocument(document)}
                              >
                                {document.title}
                                {document.is_confidential && (
                                  <span
                                    style={{
                                      marginLeft: "6px",
                                      padding: "1px 4px",
                                      background: "#fee2e2",
                                      color: "#dc2626",
                                      borderRadius: "4px",
                                      fontSize: "10px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Confidential
                                  </span>
                                )}
                              </div>
                              {document.description && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    lineHeight: "1.3",
                                  }}
                                >
                                  {document.description.length > 50
                                    ? `${document.description.substring(
                                        0,
                                        50
                                      )}...`
                                    : document.description}
                                </div>
                              )}
                            </div>

                            <div>
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  background:
                                    DocumentService.getCategoryColor(
                                      document.document_type
                                    ) + "20",
                                  color: DocumentService.getCategoryColor(
                                    document.document_type
                                  ),
                                }}
                              >
                                {DocumentService.getCategoryIcon(
                                  document.document_type
                                )}{" "}
                                {DocumentService.getCategoryLabel(
                                  document.document_type
                                )}
                              </span>
                            </div>

                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              {DocumentService.formatFileSize(
                                document.file_size
                              )}
                            </div>

                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                              {DocumentService.formatDate(document.upload_date)}
                            </div>

                            <div style={{ display: "flex", gap: "4px" }}>
                              <button
                                onClick={() => handleViewDocument(document)}
                                style={{
                                  ...buttonStyle("primary"),
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                }}
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadDocument(document)}
                                style={{
                                  ...buttonStyle("secondary"),
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                }}
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleDeleteDocument(document)}
                                style={{
                                  ...buttonStyle("danger"),
                                  padding: "4px 8px",
                                  fontSize: "11px",
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      <DocumentViewer
        patientDocument={selectedDocument}
        isOpen={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false);
          setSelectedDocument(null);
        }}
      />

      <style>
        {`
         @keyframes spin {
           0% { transform: rotate(0deg); }
           100% { transform: rotate(360deg); }
         }
         
         button:hover:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
         }
         
         input:focus, select:focus {
           border-color: #f59e0b !important;
           box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1) !important;
         }

         @media (max-width: 768px) {
           button:hover:not(:disabled) {
             transform: none;
             box-shadow: none;
           }
         }
       `}
      </style>
    </>
  );
};

export default DocumentManager;
